var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState } from 'react';
import MuiBox from '@mui/material/Box';
import MuiChip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CloseSolid } from '../../assets/icons/CloseSolid';
import { Spacing } from '../../constants';
import AccordionGroup from './AccordionGroup';
import { AccordionGroupSelectedState } from './types';
export function AccordionSelect(_a) {
    var { name, label, placeholder, defaultValue, structuredOptions, onChange } = _a, rest = __rest(_a, ["name", "label", "placeholder", "defaultValue", "structuredOptions", "onChange"]);
    const [isOpen, setIsOpen] = useState(false);
    const [selections, setSelections] = useState(defaultValue || []);
    const currentlyOpenGroups = defaultValue.reduce((acc, selection) => {
        const group = selection.split('|')[0];
        return acc.includes(group) ? acc : [...acc, group];
    }, []);
    const [openGroups, setOpenGroups] = useState(currentlyOpenGroups);
    useEffect(() => {
        onChange(selections);
    }, [selections, onChange]);
    const toggleAccordionVisibility = (groupName) => {
        openGroups.includes(groupName)
            ? setOpenGroups(openGroups.filter((group) => group !== groupName))
            : setOpenGroups([...openGroups, groupName]);
    };
    const toggleItem = (item) => {
        selections.includes(item)
            ? setSelections(selections.filter((selected) => selected !== item))
            : setSelections([...selections, item]);
    };
    const toggleGroup = (groupName) => {
        const groupItemsWithGroup = structuredOptions[groupName].map((item) => `${groupName}|${item}`);
        if (getGroupSelectedState(groupName) === AccordionGroupSelectedState.All) {
            setSelections(selections.filter((selected) => !groupItemsWithGroup.includes(selected)));
        }
        else {
            setSelections(selections.concat(groupItemsWithGroup.filter((item) => !selections.includes(item))));
        }
    };
    const getGroupSelectedState = (groupName) => {
        const compositeItemNames = structuredOptions[groupName].map((item) => `${groupName}|${item}`);
        if (compositeItemNames.every((item) => selections.includes(item))) {
            return AccordionGroupSelectedState.All;
        }
        else if (compositeItemNames.some((item) => selections.includes(item))) {
            return AccordionGroupSelectedState.Some;
        }
        else {
            return AccordionGroupSelectedState.None;
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(TextField, Object.assign({ select: true, fullWidth: true, "data-testid": "accordion-select", label: label, id: "accordion-select", sx: {
                '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                },
            }, SelectProps: {
                multiple: true,
                open: isOpen,
                onOpen: () => setIsOpen(true),
                onClose: () => setIsOpen(false),
                displayEmpty: true,
                renderValue: () => {
                    if (selections.length === 0) {
                        return React.createElement(Typography, { color: "typographyLightSecondary" }, placeholder);
                    }
                    else {
                        return (React.createElement(MuiBox, { display: "flex", flexWrap: "wrap", gap: 0.5, mr: Spacing.large }, selections.map((value) => (React.createElement(MuiChip, { key: value, label: value.split('|')[1], onDelete: () => {
                                toggleItem(value);
                            }, deleteIcon: React.createElement(CloseSolid, { style: { color: 'black' } }), onMouseDown: (e) => e.stopPropagation(), "data-testid": `accordion-chip-${value}` })))));
                    }
                },
            }, value: selections }, rest),
            React.createElement(MuiBox, { maxHeight: "500px" }, Object.keys(structuredOptions).map((group) => {
                return (React.createElement(AccordionGroup, { key: group, "data-testid": `accordion-group-${group}`, groupName: group, itemNames: structuredOptions[group], selectedItems: selections, groupSelectedState: getGroupSelectedState(group), expanded: openGroups.includes(group), toggleAccordionVisibility: () => toggleAccordionVisibility(group), toggleItem: toggleItem, toggleGroup: () => toggleGroup(group) }));
            }))),
        selections.map((s) => (React.createElement("input", { key: s, type: "hidden", value: s, name: `${name}[]` })))));
}
